import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Tabs, Tab, Spinner, Alert, Button, Badge, Modal, Form } from 'react-bootstrap';
import { productApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth'; // Import useAuth hook
import { 
  FaLandmark, 
  FaTag, 
  FaEnvelope, 
  FaTwitter, 
  FaFacebookF, 
  FaWhatsapp, 
  FaShareAlt, 
  FaRupeeSign, 
  FaCreditCard, 
  FaInfoCircle 
} from 'react-icons/fa';
import './Products.css';

const DSOProducts = () => {
  const [productsByCategory, setProductsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeKey, setActiveKey] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareEmailError, setShareEmailError] = useState('');

  const { getCurrentUser } = useAuth(); // Get access to the current user

  useEffect(() => {
    document.title = 'DSO Products';

    const fetchProducts = async () => {
      setLoading(true);
      setError('');
      try {

        const currentUser = getCurrentUser();
        const dsoUserId = currentUser?.id; // Assuming user.id is the dso_id
        
        if (!dsoUserId) {
          throw new Error('DSO ID not found. Please ensure you are logged in.');
        }

        const response = await productApi.getProducts({ limit: 500, dso_user_id: dsoUserId }); // Pass dso_id
        const products = response.data.items || [];

        // Group products by category name
        const grouped = products.reduce((acc, product) => {
          const categoryName = product.category?.category || 'Uncategorized';
          if (!acc[categoryName]) {
            acc[categoryName] = [];
          }
          acc[categoryName].push(product);
          return acc;
        }, {});

        setProductsByCategory(grouped);
        
        // Set first category as active by default
        if (Object.keys(grouped).length > 0) {
          setActiveKey(Object.keys(grouped)[0]);
        }

      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err.response?.data?.detail || err.message || 'Failed to load products.');
        if (err.message.includes('DSO ID not found')) {
          // Optionally handle specific error, e.g., redirect to login
          // Or just display the error message
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [getCurrentUser]); // Add getCurrentUser to dependency array

  const handleShareClick = (product) => {
    setCurrentProduct(product);
    setShowShareModal(true);
  };

  const handleShareEmailSubmit = (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(shareEmail)) {
      setShareEmailError('Please enter a valid email address');
      return;
    }
    
    // Here you would implement the actual email sharing functionality
    // For now, we'll just log it and close the modal
    console.log(`Sharing ${currentProduct.name} via email to ${shareEmail}`);
    setShareEmail('');
    setShareEmailError('');
    setShowShareModal(false);
  };

  const handleSocialShare = (platform) => {
    if (!currentProduct?.apply_url) return;
    
    let shareUrl = '';
    const text = `Check out this ${currentProduct.name} from ${currentProduct.bank.bank}`;
    const url = encodeURIComponent(currentProduct.apply_url);
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + currentProduct.apply_url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank');
  };

  const renderProductCard = (product) => {
    // Parse attributes if it's a JSON string
    let attributesObj = {};
    if (product.attributes && typeof product.attributes === 'string') {
      try {
        attributesObj = JSON.parse(product.attributes);
      } catch (e) {
        console.error("Failed to parse product attributes:", e);
      }
    }

    return (
      <Col key={product.id} md={6} lg={4} className="mb-4">
        <Card className="h-100 product-card">
          {product.product_image_url && (
            <div className="product-image-container">
              <img 
                src={product.product_image_url} 
                alt={product.name} 
                className="product-image" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/static/images/Wishfin-logo.png'; // Fallback image
                }}
              />
            </div>
          )}
          <Card.Body className="d-flex flex-column">
            <Card.Title className="product-card-title">{product.name}</Card.Title>
            
            <div className="product-card-meta mb-3">
              {product.bank && (
                <Badge bg="light" text="dark" className="meta-badge me-2">
                  <FaLandmark size={14} className="me-1" /> {product.bank.bank}
                </Badge>
              )}
              {product.credit_card_type && (
                <Badge bg="light" text="dark" className="meta-badge">
                  <FaCreditCard size={14} className="me-1" /> {product.credit_card_type}
                </Badge>
              )}
            </div>
            
            <div className="product-details">
              {product.interest_rate && (
                <div className="detail-item">
                  <strong>Interest Rate:</strong> {product.interest_rate}
                </div>
              )}
              
              {product.fees && (
                <div className="detail-item">
                  <strong>Fees:</strong> {product.fees}
                </div>
              )}
              
              {product.annual_fees && (
                <div className="detail-item">
                  <strong>Annual Fees:</strong> {product.annual_fees}
                </div>
              )}
              
              {(product.dso_commission_percentage || product.dso_commission_amount) && (
                <div className="detail-item commission-info">
                  <strong className='me-2'>Commission:</strong> 
                  <Badge>
                  {product.dso_commission_percentage ? 
                    `${product.dso_commission_percentage}%` : 
                    <><FaRupeeSign size={12} /> {product.dso_commission_amount}</>
                  }
                  </Badge>
                </div>
              )}
              
              {product.attributes && (
                <div className="detail-item">
                  <strong>Attributes:</strong> 
                  <div className="attributes-list">
                    {product.attributes}
                  </div>
                </div>
              )}
            </div>
            
            {product.features && product.features.length > 0 && (
              <div className="product-features mt-3">
                <h6>Key Features:</h6>
                <ul>
                  {product.features.map(feature => (
                    <li key={feature.id}>
                      <strong>{feature.feature_name}:</strong> {feature.feature_value}
                      {feature.feature_description && (
                        <div className="feature-description">
                          {feature.feature_description}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="mt-auto d-flex justify-content-between align-items-center">
              {product.apply_url && (
                <Button 
                  variant="primary" 
                  href={product.apply_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="me-2"
                >
                  Apply Now
                </Button>
              )}
              
              {product.apply_url && (
                <Button 
                  variant="outline-secondary" 
                  onClick={() => handleShareClick(product)}
                >
                  <FaShareAlt /> Share
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  if (loading) {
    return (
      <Container fluid className="p-4 d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading Products...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const categories = Object.keys(productsByCategory).sort();

  if (categories.length === 0) {
    return (
      <Container fluid className="p-4">
        <Alert variant="info">No products found.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4 dso-products-page">
      <h2 className="page-title mb-4">Available Products</h2>

      <Tabs
        activeKey={activeKey}
        onSelect={(k) => setActiveKey(k)}
        className="mb-4 product-category-tabs"
      >
        {categories.map((categoryName) => (
          <Tab 
            eventKey={categoryName} 
            title={`${categoryName} (${productsByCategory[categoryName].length})`} 
            key={categoryName}
          >
            <Row>
              {productsByCategory[categoryName].map(renderProductCard)}
            </Row>
          </Tab>
        ))}
      </Tabs>

      {/* Share Modal */}
      <Modal show={showShareModal} onHide={() => setShowShareModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Share Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentProduct && (
            <>
              <h5>{currentProduct.name}</h5>
              <p className="mb-4">Share this product with others:</p>
              
              <div className="share-links mb-4 d-flex justify-content-between">
                <Button 
                  variant="outline-primary" 
                  onClick={() => handleSocialShare('twitter')}
                  className="share-btn"
                >
                  <FaTwitter /> Twitter
                </Button>
                
                <Button 
                  variant="outline-primary" 
                  onClick={() => handleSocialShare('facebook')}
                  className="share-btn"
                >
                  <FaFacebookF /> Facebook
                </Button>
                
                <Button 
                  variant="outline-success" 
                  onClick={() => handleSocialShare('whatsapp')}
                  className="share-btn"
                >
                  <FaWhatsapp /> WhatsApp
                </Button>
              </div>
              
              <div className="mt-4">
                <h6>Share via Email</h6>
                <Form onSubmit={handleShareEmailSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Recipient Email</Form.Label>
                    <Form.Control 
                      type="email" 
                      placeholder="Enter email address" 
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      isInvalid={!!shareEmailError}
                    />
                    <Form.Control.Feedback type="invalid">
                      {shareEmailError}
                    </Form.Control.Feedback>
                  </Form.Group>
                  
                  <Button variant="primary" type="submit">
                    <FaEnvelope className="me-1" /> Send Email
                  </Button>
                </Form>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default DSOProducts;